import { useEffect, useState } from "react";
import { api } from "../api/client";
import { announceUserPatch } from "../auth/useCurrentUser";
import { theme, card, btnPrimary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { AccountSecurity } from "../components/AccountSecurity";
import { LanguagePreference } from "../components/LanguagePreference";

interface UserProfile {
  id: string;
  displayName: string;
  email: string;
  role: string;
  isOrgAdmin: boolean;
  clientRole: string | null;
  consultantRole: string | null;
  auditorRole: string | null;
  orgRole: { id: string; name: string } | null;
  primaryGrade: string | null;
  hasLocalPassword: boolean;
  totpEnabled: boolean;
  recoveryCodesLeft: number;
}

const GRADE_OPTIONS = [
  { value: "",           label: "— Not set (won't appear in client booking calendar) —" },
  { value: "senior",     label: "Senior — strategic advisory, architecture, senior risk reviews" },
  { value: "consultant", label: "Consultant — control implementation, documentation, gap analysis" },
  { value: "junior",     label: "Junior — evidence gathering, template population, admin support" },
];

function RoleBadge({ role, sub }: { role: string; sub?: string | null }) {
  const style: Record<string, { bg: string; color: string }> = {
    client:     { bg: "var(--c-accentBg)",     color: "var(--c-accentText)" },
    consultant: { bg: "var(--c-consultantBg)", color: "var(--c-consultant)" },
    auditor:    { bg: "var(--c-auditorBg)",    color: "var(--c-auditor)" },
  };
  const s = style[role] ?? { bg: "var(--c-panelAlt)", color: "var(--c-muted)" };
  const label = sub ? `${role} · ${sub.replace(/_/g, " ")}` : role;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 3,
      background: s.bg, color: s.color, textTransform: "capitalize",
    }}>
      {label}
    </span>
  );
}

export function ProfilePage() {
  const [user, setUser]           = useState<UserProfile | null>(null);
  const [loading, setLoading]     = useState(true);
  const [editName, setEditName]   = useState("");
  const [saving, setSaving]       = useState(false);
  const [nameMsg, setNameMsg]     = useState<{ ok: boolean; text: string } | null>(null);
  const [loadError, setLoadError] = useState(false);

  const [editGrade, setEditGrade]   = useState<string>("");
  const [savingGrade, setSavingGrade] = useState(false);
  const [gradeMsg, setGradeMsg]     = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api.get<{ user: UserProfile }>("/auth/me")
      .then((d) => {
        setUser(d.user);
        setEditName(d.user.displayName);
        setEditGrade(d.user.primaryGrade ?? "");
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  async function saveName() {
    if (!editName.trim() || editName.trim() === user?.displayName) return;
    setSaving(true);
    setNameMsg(null);
    try {
      const { user: updated } = await api.patch<{ user: UserProfile }>("/auth/profile", { displayName: editName.trim() });
      setUser((prev) => prev ? { ...prev, displayName: updated.displayName } : prev);
      announceUserPatch({ displayName: updated.displayName });
      setNameMsg({ ok: true, text: "Name updated." });
    } catch {
      setNameMsg({ ok: false, text: "Failed to update name." });
    } finally {
      setSaving(false);
    }
  }

  async function saveGrade() {
    setSavingGrade(true);
    setGradeMsg(null);
    try {
      const { user: updated } = await api.patch<{ user: UserProfile }>("/auth/profile", {
        primaryGrade: editGrade || null,
      });
      setUser((prev) => prev ? { ...prev, primaryGrade: updated.primaryGrade } : prev);
      setGradeMsg({ ok: true, text: "Booking grade saved." });
    } catch {
      setGradeMsg({ ok: false, text: "Failed to save grade." });
    } finally {
      setSavingGrade(false);
    }
  }

  const subRole = user?.orgRole?.name ?? user?.clientRole ?? user?.consultantRole ?? user?.auditorRole ?? null;

  if (loading) return <LoadingBlock />;

  if (loadError) {
    return (
      <div style={{ padding: "20px 24px" }}>
        <div style={{ ...card, color: theme.color.dangerText }}>
          Could not load your profile. Please refresh the page or sign in again.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px" }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, color: theme.color.heading, margin: "0 0 4px" }}>
        My Profile
      </h1>
      <p style={{ fontSize: 13, color: theme.color.subtle, margin: "0 0 24px" }}>
        Manage your account details.
      </p>

      {/* Identity card */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.color.muted, marginBottom: 14 }}>
          Account
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Display name */}
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 5 }}>
              Display name
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={editName}
                onChange={(e) => { setEditName(e.target.value); setNameMsg(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
                style={{ ...inputStyle, flex: 1 }}
                maxLength={100}
              />
              <button
                onClick={saveName}
                disabled={saving || editName.trim() === user?.displayName || !editName.trim()}
                style={{ ...btnPrimary, opacity: saving || editName.trim() === user?.displayName || !editName.trim() ? 0.5 : 1, whiteSpace: "nowrap" }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
            {nameMsg && (
              <div style={{ fontSize: 12, marginTop: 6, color: nameMsg.ok ? theme.color.successText : theme.color.dangerText }}>
                {nameMsg.text}
              </div>
            )}
          </div>

          {/* Email (read-only) */}
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 5 }}>
              Email
            </label>
            <div style={{ ...inputStyle, color: theme.color.subtle, background: "var(--c-panelAlt)", cursor: "default" }}>
              {user?.email}
            </div>
          </div>

          {/* Role */}
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 5 }}>
              Role
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <RoleBadge role={user?.role ?? ""} sub={subRole} />
              {user?.isOrgAdmin && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 3, background: "var(--c-warningBg)", color: "var(--c-warningText)" }}>
                  Org admin
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking grade — consultants only */}
      {user?.role === "consultant" && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.color.muted, marginBottom: 14 }}>
            Booking Grade
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12, lineHeight: 1.6 }}>
            Sets the grade at which clients can book you. Senior consultants can also be booked for consultant and junior work. Without a grade set, you won't appear in the client booking calendar.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={editGrade}
              onChange={(e) => { setEditGrade(e.target.value); setGradeMsg(null); }}
              style={{ ...inputStyle, flex: 1, minWidth: 220, cursor: "pointer" }}
            >
              {GRADE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={saveGrade}
              disabled={savingGrade || editGrade === (user?.primaryGrade ?? "")}
              style={{ ...btnPrimary, opacity: savingGrade || editGrade === (user?.primaryGrade ?? "") ? 0.5 : 1, whiteSpace: "nowrap" }}
            >
              {savingGrade ? "Saving…" : "Save grade"}
            </button>
          </div>
          {gradeMsg && (
            <div style={{ fontSize: 12, marginTop: 6, color: gradeMsg.ok ? theme.color.successText : theme.color.dangerText }}>
              {gradeMsg.text}
            </div>
          )}
        </div>
      )}

      <LanguagePreference />

      {/* Password & MFA — live for password accounts, a pointer to the IdP otherwise */}
      {user && (
        <AccountSecurity
          user={user}
          onChanged={() => {
            api.get<{ user: UserProfile }>("/auth/me").then((d) => setUser(d.user)).catch(() => { /* keep what we have */ });
          }}
        />
      )}
    </div>
  );
}
